import { MultiPolygon, Polygon, Feature, FeatureCollection } from "geojson";

export type UUID = string;

/**
 * Groups: Vector data layer or Raster Data Layer.
 */
export interface DataLayerGroup {
	id: UUID;
	name: string;
}

/**
 * Types: Preset options for data layer types: Productivity Zones, EC Zones, As Applied, As Treated,
 * etc.
 */
export interface DataLayerType {
	id: UUID;
	name: string;
	dataLayerGroupId: UUID;
	abbreviation?: string;
	description?: string;
	presetName?: string;
}

/**
 * Options for UI flow.
 */
export interface DataLayerOptions {
	dataLayerGroups: DataLayerGroup[];
	dataLayerTypes: DataLayerType[];
}

/**
 * Model for a record in DataLayer table
 */
export interface DataLayer {
	id: UUID;
	name: string;
	plan?: string;
	dateAndTime: Date;
	clientId: UUID;
	dataLayerGroupId: UUID;
}

/**
 * Request type for creating a new DataLayer via POST (omit id which is generated)
 */
export type CreateDataLayerRequest = Omit<DataLayer, "id">;

/**
 *  Request type for updating a DataLayer via PUT
 */
export type UpdateDataLayerRequest = DataLayer;

/**
 * Model for a record in DataLayerUploads table
 */
export interface DataLayerUpload {
	id: UUID;
	dataLayerId: UUID;
	dataLayerTypeId: UUID;
	vectorDataAttributeName?: string;
	vectorDataNumZones?: number;
	rawDataUrlPath: string;
	createdDateTimeUtc: Date;
	urlPath?: string;
	fieldId?: UUID;
	trialId?: UUID;
	plotId?: UUID;
	stdDataSasUrl?: string;
	preprocessedDataSasUrl?: string;
	rawDataSasUrl?: string;
}

/**
 * Types of data which can appear in GeoJSON property values.
 */
export type AttributeTableValue = string | number | boolean;

/**
 * Mapping of source data attribute field value to standard attribute field key, value.
 */
export type VectorDataLayerValuesMapping = [AttributeTableValue, number, string | null];

/**
 * Request data model for PUT (process upload to standard GeoJSON format)
 */
export interface VectorDataLayerRequest {
	uuid: UUID;
	layerNumber: number;
	attributeFieldName: string;
	numZones: number;
	valuesMapping: VectorDataLayerValuesMapping[];
}

// Response type for GET Vectors/{uuid}
export interface VectorDataLayer {
	uuid: UUID;
	dataLayerId: UUID;
	dataLayerTypeId: UUID;
	attributeFieldName?: string;
	urlPath?: string;
	// Add other fields as needed based on actual API response
}

// Request type for POST to Vectors or Rasters (multipart/form-data)
export interface CreateDataLayerUploadRequest {
	dataLayerId: UUID;
	dataLayerTypeId: UUID;
	file: File;
}

export interface DataLayerTypeSelectOption {
	key: string;
	value: string;
	text: string;
}

/**
 * Enum of allowed vector data formats.
 */
export enum VectorDataFormat {
	GEOPACKAGE = "geopackage",
	SHAPEFILE = "shapefile",
	GEOJSON = "geojson",
	UNSUPPORTED = "(unsupported)",
}

/**
 * Enum of allowed data types for attribute table. Needed for coordination between
 * typescript and python API (geopandas) data models.
 */
export enum DataType {
	STRING = "string",
	INT = "number (integer)",
	FLOAT = "number (decimal)",
	BOOL = "boolean",
	DATE = "string (ISO-8601 date)",
}

/**
 * Data model for list of column names and data types in attribute table.
 */
export interface AttributeTableFields {
	fields: string[];
	dataTypes: DataType[];
	nullsDetected: boolean[];
}

/**
 * Data model for attribute table values, represented as a list of rows. Each row is a dictionary
 * mapping column names to values.
 */
export interface AttributeTable {
	fields: AttributeTableFields;
}

/**
 * Data model for coordinate reference system ID and name.
 */
export interface CRS {
	name: string;
	epsgCode: number;
}

/**
 * Data model for enhancing GeoJSON feature collection with metadata about the phase 1 (POST) upload
 * workflow. This metadata describes the raw GIS file upload.
 */
export interface PreprocessedFeatureCollectionProps {
	id: UUID;
	createdDateTimeUtc: Date;
	dataLayerId: string;
	dataLayerTypeId: string;
	vectorDataFormat?: VectorDataFormat;
	fileNames: string[];
	featureCount?: number[];
	layerNames?: string[];
	crs?: CRS[];
	geometryTypes?: string[][];
	attributeTables?: AttributeTable[];
	isValid?: boolean[];
}

/**
 * Data model for enhancing the standard GeoJSON feature collection with provenance metadata and
 * about the raw to standard transformation.
 */
export interface StdFeatureCollectionProps {
	id: UUID;
	rawLayerNumber: number;
	rawAttributeFieldName: string;
	rawValuesMapping: VectorDataLayerValuesMapping[];
	attributeDescriptions: string[];
}

/**
 * GeoJSON Feature properties for standard aerialFARMS zones/categories. See also
 * .attributeDescriptions in toplevel GeoJSON properties.
 */
export interface StdFeatureProps {
	/**
	 * aerialFARMS category ID
	 */
	aerialfarmsStd: number;
	/**
	 * Area in square meters- calculated by API
	 */
	areaM2: number;
	/**
	 * Category description, defaults to either catValueNum or catValueText if user didn't enter
	 * custom description.
	 */
	catDesc: string;
	/**
	 * Numeric value for category. Area-weighted average in case of feature dissolve.
	 */
	catValueNum: number | null;
	/**
	 * String value for category.
	 */
	catValueText: string | null;
}

/**
 * Upload of raw vector data, preprocessing result
 */
export type VectorDataLayerUploadPreprocResult = GeoJSON.FeatureCollection<Polygon | MultiPolygon>;

/**
 * Standardized GeoJSON format for vector data layer
 */
export type VectorDataLayerUploadStdResult = GeoJSON.FeatureCollection<Polygon | MultiPolygon>;

export interface DataLayerPlotAssociationCounts {
	[factorName: string]: {
		[factorTreatmentName: string]: {
			[fieldId: string]: number;
		};
	};
}

export type PlotResultsInfo = [string[][], DataLayerPlotAssociationCounts];

/**
 * Response data model for SearchFieldsByDataLayer endpoint.
 */
export interface FieldWithBoundaryAndPlots {
	fieldId: UUID;
	clientId: UUID;
	flightId: UUID;
	fieldName: string;
	growingSeasonName: string;
	fieldBoundary: Feature<Polygon>;
	plotBoundaries: FeatureCollection<Polygon>;
}

export interface DataLayerDropdownInfo {
	factorId: UUID;
	factorName: string;
	default: boolean;
	dataLayerUploadGeometrySasUrl: string;
	plotAssociatedGeometrySasUrl: string;
	plotMap: { [plotId: UUID]: number };
}
